import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { createPath, useLocation, useMatches, useNavigationType } from 'react-router-dom';
import { getRouteConfig, Menu, Pages, SettingsMenu } from 'constants/menu';
import { z } from 'zod';

const navigationEntrySchema = z.object({
  location: z.object({
    pathname: z.string(),
    search: z.string(),
    hash: z.string(),
    state: z.unknown(),
    key: z.string(),
  }),
  scope: z.string().optional(),
  agencyId: z.string().optional(),
  title: z.string(),
  get back() {
    return navigationEntrySchema.optional();
  },
});

type NavigationEntry = z.infer<typeof navigationEntrySchema>;

const NavigationHistoryContext = createContext<NavigationEntry | undefined>(undefined);
const storageKey = 'agx-staff-navigation-history';

const readNavigationEntries = (): Map<string, NavigationEntry> => {
  try {
    const storedEntries = sessionStorage.getItem(storageKey);
    if (!storedEntries) return new Map();

    return new Map(
      z.array(z.tuple([z.string(), navigationEntrySchema])).parse(JSON.parse(storedEntries))
    );
  } catch {
    return new Map();
  }
};

const findNavigationEntry = (
  entries: Map<string, NavigationEntry>,
  location: NavigationEntry['location']
) => {
  const entry = entries.get(location.key);
  return entry && createPath(entry.location) === createPath(location) ? entry : undefined;
};

export const NavigationHistoryProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const matches = useMatches();
  const navigationType = useNavigationType();
  const { agencyId, campaignId } = matches.at(-1)!.params;
  const isAgencyPage = location.pathname.startsWith(`/${Pages.AGENCIES}/`) && !!agencyId;
  const agencySection = location.pathname.split('/')[3];
  const scope = campaignId
    ? 'campaign'
    : agencyId
      ? `${isAgencyPage ? 'agency' : 'partner'}:${agencyId}`
      : undefined;
  const routeConfig = getRouteConfig(location.pathname);
  const title = isAgencyPage
    ? ([...SettingsMenu, ...Menu].find(({ pageLink }) => pageLink === agencySection)?.title ??
      'Agency')
    : (routeConfig?.title ?? routeConfig?.activeLink ?? 'All Tasks');
  const entry: NavigationEntry = {
    location,
    scope,
    agencyId: isAgencyPage ? agencyId : undefined,
    title,
  };
  const [history, setHistory] = useState(() => {
    const entries = readNavigationEntries();
    const current = { ...entry, back: findNavigationEntry(entries, location)?.back };
    entries.set(location.key, current);
    return { current, entries };
  });

  // Keep the current tab's return destinations available after a page reload.
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify([...history.entries]));
    } catch {
      // Navigation still works when browser storage is unavailable.
    }
  }, [history.entries]);

  if (history.current.location !== location) {
    const previous = history.current;
    const restored =
      navigationType === 'POP' ? findNavigationEntry(history.entries, location) : undefined;
    let back: NavigationEntry | undefined;

    if (restored) {
      back = restored.back;
    } else if (scope) {
      if (scope === previous.scope) {
        back = previous.back;
      } else if (previous.back?.scope === scope) {
        // Returning to the entry page restores its own outer destination.
        back = previous.back.back;
      } else if (previous.scope === 'campaign') {
        back = previous.back;
      } else {
        back = previous;
      }
    }

    const current = { ...entry, back };
    const entries = new Map(history.entries);
    if (navigationType === 'REPLACE') entries.delete(previous.location.key);
    entries.set(location.key, current);
    setHistory({ current, entries });
  }

  return (
    <NavigationHistoryContext.Provider value={history.current.back}>
      {children}
    </NavigationHistoryContext.Provider>
  );
};

export const useNavigationHistory = () => {
  const back = useContext(NavigationHistoryContext);

  return {
    to: back?.location ?? `/${Pages.ALL_TASKS}`,
    state: back?.location.state,
    title: back?.title ?? 'All Tasks',
    agencyId: back?.agencyId,
  };
};
